/**
 * What a slot-settings dropdown actually offers.
 *
 * Two independent sources feed the same <Select>, and they disagree in shape:
 *
 *   - `fieldMetadataComplete.js`  ->  { value, label }
 *   - the server's V1 metadata    ->  { value, text }
 *
 * The server's list is generated from `slot-settings2.html`, so it is the authority on what V1
 * offers - including options V1 has commented out (#816 UI layout is down to one live option,
 * #612 reposting source has DIRECTPOSTURLS commented away). Reading only `.label` rendered every
 * V1-sourced dropdown as blank rows: selectable, unreadable.
 *
 * The other half of this file's reason to exist is what it REFUSES to do: invent options. Two
 * fields used to be hardcoded in the page with made-up option sets -
 *
 *   #821 automationVariantType  0..3 as "Standard Automation"/"Conservative Mode"/"Aggressive
 *        Mode"/"Custom Profile", against V1's plain 1..7. No such modes exist in the product, and
 *        11,043 of 11,044 slots store 1, so nearly every slot displayed a mode it was not in.
 *   #100 aeroplaneModeToggleType  "0","2","3","4" against V1's "default",
 *        "2_toggleJustMobileData", "3_viaUI", "4_directSwipeDowns" - a different value space, so
 *        saving from V3 wrote something V1's own select could not display.
 *
 * If a dropdown needs options, they come from V1 or from metadata. Never from here.
 */

/** One option in the shape <SelectItem> needs: string value, human-readable label. */
function normalizeOption(opt) {
  if (typeof opt === 'string') return { value: opt, label: opt };
  if (!opt || opt.value === undefined || opt.value === null) return null;
  // `label` (local metadata) or `text` (server/V1). Fall back to the value so an option is never
  // rendered as an empty row - a blank choice is worse than an ugly one.
  const value = String(opt.value);
  const label = opt.label ?? opt.text ?? value;
  return { value, label: String(label) === '' ? value : String(label) };
}

/**
 * Resolve a field's dropdown options.
 *
 * @param meta       entry from FIELD_METADATA; `type: "select"` + `options` wins when present
 * @param v1Options  options the server reported for this setting id, from V1's own template
 * @returns array of { value, label }; empty when the field is not a dropdown
 */
export function selectOptions(meta, v1Options) {
  const raw = (meta && meta.type === 'select' && meta.options) ? meta.options : v1Options;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeOption).filter(Boolean);
}

/**
 * Which option the control should show.
 *
 * A stored value always wins, even one absent from the list - that is how a slot on a retired
 * option (the one slot storing automationVariantType 5) keeps showing what it really is instead
 * of silently reading as something else. Only a genuinely empty value falls back to the first
 * option, matching what V1's own <select> does with no selection.
 */
export function currentOptionValue(value, options) {
  if (value !== null && value !== undefined && String(value) !== '') return String(value);
  return options.length ? options[0].value : '';
}
